import Container = PIXI.Container;
import { AbstractPopUp } from '../../popUps/AbstractPopUp';
import { SceneManager } from './SceneManager';
import { LoadingPopUp } from '../../popUps/LoadingPopUp';
import { ConfirmPopUp } from '../../popUps/ConfirmPopUp';
import { SettingsPopUp } from '../../popUps/SettingsPopUp';
import { LosePopUp } from '../../popUps/LosePopUp';
import { NoLivesPopUp } from '../../popUps/NoLivesPopUp';
import { createSprite } from '../../utils/GameEngine';
import Sprite = PIXI.Sprite;
import { MessagePopUp } from '../../popUps/MessagePopUp';
import { clearAnimations } from '../../utils/Animations';
import { RetryConfirmPopup } from '../../popUps/RetryConfirmPopup';
import { DailyRewardPopup } from '../../popUps/DailyRewardPopup';
import { CheatPopup } from '../../popUps/CheatPopUp';
import { LevelWinPopup } from '../../popUps/winPopUp/LevelWinPopup';
import { ChapterWinPopup } from '../../popUps/winPopUp/ChapterWinPopup';
import { EndPopup } from '../../popUps/EndPopup';
import { PreAdPopup } from '../../popUps/PreAdPopup';
import { ThankYouPopup } from '../../popUps/ThankYouPopup';

export enum PopUpName {
  Loading = 'Loading',
  ConfirmPopUp = 'ConfirmPopUp',
  SettingsPopUp = 'SettingsPopUp',
  LosePopUp = 'LosePopUp',
  NoLivesPopUp = 'NoLivesPopUp',
  BuyResourcePopUp = 'BuyResourcePopUp',
  MessagePopUp = 'MessagePopUp',
  ChapterWinPopup = 'ChapterWinPopup',
  LevelWinPopup = 'LevelWinPopup',
  RetryConfirmPopup = 'RetryConfirmPopup',
  DailyRewardPopup = 'DailyRewardPopup',
  CheatPopup = 'CheatPopup',
  EndPopup = 'EndPopup',
  PreAdPopup = 'PreAdPopup',
  ThankYouPopup = 'ThankYouPopup',
}

export class PopUpManager {
  private static _instance: PopUpManager;
  private stage: Container = SceneManager.getInstance().stage;

  private popups: Map<PopUpName, AbstractPopUp> = new Map<PopUpName, AbstractPopUp>();
  private activePopUp: AbstractPopUp;
  private overlay: Sprite;
  private lastPopUp: AbstractPopUp;
  private defaultOverlayAlpha: number = 0.5;

  constructor() {
    this.overlay = this.createUnderlay();
  }

  public getView(windowName: PopUpName, params?: any, forceNew: boolean = false): AbstractPopUp {
    if (!params) {
      params = {};
    }
    if (forceNew && this.popups[windowName]) {
      this.destroyWindow(windowName);
    }

    if (this.popups[windowName]) {
      return this.popups[windowName];
    } else {
      let newWindow: AbstractPopUp;
      switch (windowName) {
        case PopUpName.Loading: {
          newWindow = new LoadingPopUp(windowName, params);
          break;
        }
        case PopUpName.NoLivesPopUp: {
          newWindow = new NoLivesPopUp(windowName, params);
          break;
        }
        case PopUpName.ConfirmPopUp: {
          newWindow = new ConfirmPopUp(windowName, params);
          break;
        }
        case PopUpName.SettingsPopUp: {
          newWindow = new SettingsPopUp(windowName, params);
          break;
        }
        case PopUpName.LosePopUp: {
          newWindow = new LosePopUp(windowName, params);
          break;
        }
        case PopUpName.ThankYouPopup: {
          newWindow = new ThankYouPopup(windowName, params);
          break;
        }
        case PopUpName.MessagePopUp: {
          newWindow = new MessagePopUp(windowName, params);
          break;
        }
        case PopUpName.LevelWinPopup: {
          newWindow = new LevelWinPopup(windowName, params);
          break;
        }
        case PopUpName.ChapterWinPopup: {
          newWindow = new ChapterWinPopup(windowName, params);
          break;
        }
        case PopUpName.CheatPopup: {
          newWindow = new CheatPopup(windowName, params);
          break;
        }
        case PopUpName.RetryConfirmPopup: {
          newWindow = new RetryConfirmPopup(windowName, params);
          break;
        }
        case PopUpName.DailyRewardPopup: {
          newWindow = new DailyRewardPopup(windowName, params);
          break;
        }
        case PopUpName.EndPopup: {
          newWindow = new EndPopup(windowName, params);
          break;
        }
        case PopUpName.PreAdPopup: {
          newWindow = new PreAdPopup(windowName, params);
          break;
        }
      }
      this.popups[windowName] = newWindow;
      newWindow.onRegister();
      return newWindow;
    }
  }

  public static getInstance(): PopUpManager {
    return this._instance || (this._instance = new this());
  }

  public animateOverlayOut(duration: number): void {
    if (!this.lastPopUp) {
      TweenLite.to(this.overlay, duration, {
        alpha: 0,
        onComplete: (): void => {
          this.overlay.alpha = this.defaultOverlayAlpha;
          this.overlay.visible = false;
        }
      });
    }
  }

  public animateOverlayIn(duration: number): void {
    this.overlay.alpha = 0;
    TweenLite.to(this.overlay, duration, {
      alpha: this.defaultOverlayAlpha,
    });
  }

  public show(name: PopUpName, keepQueue: boolean = false): void {
    if (this.activePopUp && this.activePopUp.name !== name) {
      if (keepQueue) {
        this.lastPopUp = this.activePopUp;
      } else {
        this.lastPopUp = null;
      }
      this.activePopUp.hide(keepQueue, keepQueue);
    }
    const popUp: AbstractPopUp = this.getView(name);
    this.activePopUp = popUp;
    popUp.visible = true;
    if (!popUp.params.noOverlay) {
      this.showUnderlay();
    }
    this.stage.addChildAt(popUp, this.stage.children.length);
  }

  public hide(name: PopUpName): void {
    const popUp: AbstractPopUp = this.getView(name);
    if (this.activePopUp === popUp) {
      popUp.visible = false;
      this.stage.removeChild(popUp);
      this.activePopUp = null;
    }
    this.hideUnderlay();
    if (this.lastPopUp && this.lastPopUp !== popUp) {
      this.lastPopUp.show();
      this.lastPopUp = null;
    }
  }

  public destroyWindow(name: PopUpName): void {
    this.stage.removeChild(this.popups[name]);
    clearAnimations(this.popups[name]);
    this.popups[name].destroy({children: true});
    this.popups[name] = null;
  }

  private showUnderlay(): void {
    this.overlay.visible = true;
    this.stage.addChildAt(this.overlay, this.stage.children.length);
  }

  private hideUnderlay(): void {
    this.overlay.visible = false;
    this.stage.removeChild(this.overlay);
  }

  private createUnderlay(): Sprite {
    const underlay: Sprite = createSprite('overlay_gradient');
    underlay.width = SceneManager.gameWidth;
    underlay.height = SceneManager.gameHeight;
    underlay.alpha = 0.5;
    underlay.interactive = true;

    // const overlay: Graphics = new Graphics();
    // overlay.beginFill(0xFFFFFF);
    // overlay.drawRect(0, 0, SceneManager.gameWidth, SceneManager.gameHeight);
    // overlay.endFill();
    // overlay.interactive = true;
    // overlay.alpha = this.defaultOverlayAlpha;
    // overlay.visible = false;
    return underlay;
  }
}